// This file configures the initialization of Sentry on the browser.
// The config you add here will be used whenever a page is visited.
// https://docs.sentry.io/platforms/javascript/guides/nextjs/
;globalThis["_sentryRewritesTunnelPath"] = undefined;globalThis["SENTRY_RELEASE"] = {"id":"business-online-28045245c562528afb9d1103d076e77e0480e2b4"};globalThis["_sentryBasePath"] = undefined;globalThis["_sentryRewriteFramesAssetPrefixPath"] = "/n/online";import * as Sentry from '@sentry/nextjs';

const AUTO_MANAGED_NOISE_RULES = [
  // sentry-noise:auto-rules:start
  // sentry-noise:auto-rules:end
];

function normalizeNoiseToken(value = '') {
  return String(value || '').toLowerCase().replace(/\s+/g, ' ').trim();
}

function getExceptionValues(event = {}) {
  const values = (((event || {}).exception || {}).values || []);
  return Array.isArray(values) ? values : [];
}

function getNoiseMessages(event = {}) {
  const messages = [];
  if (event?.message) {
    messages.push(String(event.message));
  }

  for (const item of getExceptionValues(event)) {
    if (item?.value) {
      messages.push(String(item.value));
    }
  }

  return messages.map(normalizeNoiseToken).filter(Boolean);
}

function getNoiseFrames(event = {}) {
  const frames = [];
  for (const item of getExceptionValues(event)) {
    const stackFrames = (((item || {}).stacktrace || {}).frames || []);
    if (!Array.isArray(stackFrames)) {
      continue;
    }
    for (const frame of stackFrames) {
      const filename = String((frame && (frame.filename || frame.absPath || frame.abs_path)) || '');
      const fn = String((frame && (frame.function || frame.module)) || '');
      const merged = [filename, fn].filter(Boolean).join('::');
      if (merged) {
        frames.push(normalizeNoiseToken(merged));
      }
    }
  }
  return frames;
}

function matchesManagedRuleTokens(candidates = [], tokens = []) {
  if (!Array.isArray(tokens) || tokens.length === 0) {
    return true;
  }

  const normalizedTokens = tokens.map(normalizeNoiseToken).filter(Boolean);
  if (normalizedTokens.length === 0) {
    return true;
  }

  return normalizedTokens.some((token) => candidates.some((candidate) => candidate.includes(token)));
}

function matchesManagedNoiseRule(event = {}, rule = {}) {
  const messages = getNoiseMessages(event);
  const frames = getNoiseFrames(event);
  const culprit = normalizeNoiseToken(String(event?.culprit || ''));
  const culprits = culprit ? [culprit] : [];

  return (
    matchesManagedRuleTokens(messages, rule.messageIncludes) &&
    matchesManagedRuleTokens(frames, rule.frameIncludes) &&
    matchesManagedRuleTokens(culprits, rule.culpritIncludes)
  );
}

function shouldDropManagedNoise(event = {}) {
  return AUTO_MANAGED_NOISE_RULES.some((rule) => matchesManagedNoiseRule(event, rule));
}

export default function (conf = {}) {
  if (process.env.NODE_ENV === 'production') {
    const { beforeSend: externalBeforeSend, ...restConf } = conf || {};

    const shouldDropScriptRedeclareNoise = (event) => {
      const values = (((event || {}).exception || {}).values || []);
      if (!Array.isArray(values) || values.length === 0) {
        return false;
      }

      const first = values[0] || {};
      const value = String(first.value || '');
      const type = String(first.type || '');
      if (type !== 'SyntaxError') {
        return false;
      }
      if (!/Identifier 'script' has already been declared/i.test(value)) {
        return false;
      }

      const frames = (((first.stacktrace || {}).frames) || []);
      if (!Array.isArray(frames) || frames.length === 0) {
        return false;
      }

      return frames.every((frame) => {
        const filename = String((frame && (frame.filename || frame.absPath)) || '');
        return filename === '<anonymous>';
      });
    };

    Sentry.init({
      dsn: process.env.NEXT_PUBLIC_SENTRY_DSN,
      // Adjust this value in production, or use tracesSampler for greater control
      tracesSampleRate: 0.05,
      environment: process.env.NEXT_PUBLIC_HEART_ENV || 'dev',
      // ...
      // Note: if you want to override the automatic release value, do not set a
      // `release` value here - use the environment variable `SENTRY_RELEASE`, so
      // that it will also get attached to your source maps
      denyUrls: [
        /track\.mm\.taou\.com/i,
        /^chrome-extension:\/\//i,
        /^moz-extension:\/\//i,
        /^safari-web-extension:\/\//i,
        /^webkit-masked-url:/i,
      ],
      ignoreErrors: [
        'ResizeObserver loop limit exceeded',
        'SyntaxError: The string did not match the expected pattern.',
        'ResizeObserver loop completed with undelivered notifications',
      ],
      integrations: [Sentry.browserTracingIntegration()],
      ...restConf,
      beforeSend(event, hint) {
        if (shouldDropManagedNoise(event) || shouldDropScriptRedeclareNoise(event)) {
          return null;
        }

        if (typeof externalBeforeSend === 'function') {
          return externalBeforeSend(event, hint);
        }

        return event;
      },
    });
  }
}
